import { Tabs, TabsProps } from "antd";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { PermissionsAPI } from "@/api";
import Query from "@/models/query";
import { useGlobalStore } from "@/stores";
import { ConfiguredByParentProvider } from "@/ui/_common_/configured-by-parent-alert";
import { ErrorHandler } from "@/ui/_common_/error-handler";
import ErrorsFormatter from "@/ui/_common_/errors-formatter";
import { FormSpinner } from "@/ui/_common_/form-spinner";
import { Bonuses } from "@/ui/bonuses";
import { Games } from "@/ui/games";
import { JackpotSettingsForm } from "@/ui/jackpots";
import { HallLobbySettings } from "@/ui/lobby";
import { PSPSetting } from "@/ui/psp-setting/form";
import { Sweepstakes } from "@/ui/sweepstakes";
import { Websites } from "@/ui/websites";
import { isTruthy } from "@/utilities";

import { HallOtherSettings } from "./hall-other-settings";
import { HallSettingsForm } from "./hall-settings-form";
import { HallSpecialSettings } from "./hall-special-settings";

interface Props {
  hallId: number;
  setHallName(name: string): void;
}

export const HallSettingsTabs = observer(({ hallId, setHallName }: Props) => {
  const intl = useIntl();
  const [configuredByParent, setConfiguredByParent] = useState(false);

  const role = useMemo(() => {
    const { userStore } = useGlobalStore();
    return userStore.user.role;
  }, []);

  const permissionsQuery = useMemo(() => {
    const query = new Query(PermissionsAPI.get);
    query.submit({ hallId });
    return query;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (permissionsQuery.isIdle || permissionsQuery.isPending) {
    return <FormSpinner />;
  }

  if (permissionsQuery.isRejected) {
    return <ErrorsFormatter queries={[permissionsQuery]} />;
  }

  return (
    <ConfiguredByParentProvider value={configuredByParent}>
      <Tabs
        destroyInactiveTabPane={true}
        items={(
          [
            {
              key: "general",
              label: intl.formatMessage({ defaultMessage: "General" }),
              children: (
                <HallSettingsForm
                  hallId={hallId}
                  setHallName={setHallName}
                  setConfiguredByParent={setConfiguredByParent}
                />
              ),
            },
            {
              key: "lobby",
              label: intl.formatMessage({ defaultMessage: "Lobby" }),
              children: (
                <HallLobbySettings
                  hallId={hallId}
                  permissionsLobby={permissionsQuery.data.lobby}
                />
              ),
            },
            permissionsQuery.data.websites && {
              key: "websites",
              label: intl.formatMessage({ defaultMessage: "Websites" }),
              children: <Websites.EditForm entity={{ hallId }} />,
            },
            permissionsQuery.data.games && {
              key: "games",
              label: intl.formatMessage({ defaultMessage: "Games" }),
              children: <Games.EditForm entity={{ hallId }} />,
            },
            permissionsQuery.data.bonuses && {
              key: "bonuses",
              label: intl.formatMessage({ defaultMessage: "Bonuses" }),
              className: "subtabs",
              children: <Bonuses.Tabs entity={{ hallId }} />,
            },
            permissionsQuery.data.jackpots && {
              key: "jackpots",
              label: intl.formatMessage({ defaultMessage: "Jackpots" }),
              children: <JackpotSettingsForm entity={{ hallId }} />,
            },

            permissionsQuery.data.sweepstakes && {
              key: "sweepstakes",
              label: intl.formatMessage({ defaultMessage: "Sweepstakes" }),
              children: <Sweepstakes.EditForm entity={{ hallId }} />,
            },
            permissionsQuery.data.other && {
              key: "other",
              label: intl.formatMessage({ defaultMessage: "Other" }),
              children: <HallOtherSettings hallId={hallId} />,
            },
            ["Admin", "Manager"].includes(role) && {
              key: "special",
              label: "Special",
              children: <HallSpecialSettings hallId={hallId} />,
            },
            ["Admin", "Manager", "ClientAdmin"].includes(role) && {
              key: "psp",
              label: intl.formatMessage({ defaultMessage: "PSP" }),
              children: (
                <PSPSetting
                  entity={{ hallId }}
                  readonly={["ClientAdmin"].includes(role)}
                />
              ),
            },
          ] satisfies Array<NonNullable<TabsProps["items"]>[0] | false>
        )
          .filter(isTruthy)
          .map((tab) => ({
            ...tab,
            children: <ErrorHandler>{tab.children}</ErrorHandler>,
          }))}
      />
    </ConfiguredByParentProvider>
  );
});
